import { Accordion, type AccordionRootProps } from "@heroui/react";
import { type AppAccordionVariant } from "./variants";

/**
 * Constrained accordion root props.
 * className escape hatch available for one-off overrides.
 */
export type AppAccordionProps = Omit<
  AccordionRootProps,
  "className" | "variant"
> & {
  /** Visual variant. @default "default" */
  variant?: AppAccordionVariant;
  /** Optional className escape hatch for one-off overrides. */
  className?: string;
};

/**
 * AppAccordion root — Design system collapsible container.
 */
function AppAccordionRoot({
  variant = "default",
  className,
  ...props
}: AppAccordionProps) {
  return <Accordion {...props} variant={variant} className={className} />;
}

/**
 * AppAccordion — Design system collapsible sections.
 *
 * Wraps HeroUI v3 Accordion with constrained Rosé Pine variants.
 * Sub-components (Item, Heading, Trigger, Panel, Indicator, Body)
 * are HeroUI pass-throughs.
 *
 * @example
 * ```tsx
 * <AppAccordion variant="surface">
 *   <AppAccordion.Item>
 *     <AppAccordion.Heading>
 *       <AppAccordion.Trigger>Section 1</AppAccordion.Trigger>
 *       <AppAccordion.Indicator />
 *     </AppAccordion.Heading>
 *     <AppAccordion.Panel>
 *       <AppAccordion.Body>Content for section 1.</AppAccordion.Body>
 *     </AppAccordion.Panel>
 *   </AppAccordion.Item>
 * </AppAccordion>
 * ```
 */
export const AppAccordion = Object.assign(AppAccordionRoot, {
  Item: Accordion.Item,
  Heading: Accordion.Heading,
  Trigger: Accordion.Trigger,
  Panel: Accordion.Panel,
  Indicator: Accordion.Indicator,
  Body: Accordion.Body,
});
